import React, { useState } from "react";
import { CalendarIcon, EditIcon, CheckIcon } from "../icons";
import DateRangePicker from "./DateRangePicker";

interface DateRangeSelectorProps {
  checkInDate: string;
  checkOutDate: string;
  onChange: (startDate: string, endDate: string) => void;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  checkInDate,
  checkOutDate,
  onChange,
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getDuration = () => {
    if (!checkInDate || !checkOutDate) return "";
    const start = new Date(checkInDate);
    const end = new Date(checkOutDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays === 1 ? "1 dzień" : `${diffDays} dni`;
  };

  const handleDateChange = (startDate: string, endDate: string) => {
    onChange(startDate, endDate);
  };

  return (
    <div className="space-y-4">
      <label className="form-label">
        Termin pobytu <span className="text-error-500">*</span>
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={() => setShowDatePicker(!showDatePicker)}
          className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-left bg-white hover:bg-neutral-50 ${
            checkInDate && checkOutDate
              ? "border-success-200 bg-success-50"
              : "border-neutral-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <CalendarIcon className="text-primary-500" />
              </div>
              <div className="text-sm text-neutral-600">
                <span className="block font-medium">Data przyjazdu</span>
                <span className="text-neutral-900 font-semibold">
                  {formatDate(checkInDate) || "Wybierz datę"}
                </span>
              </div>
              <div className="text-neutral-400">→</div>
              <div className="text-sm text-neutral-600">
                <span className="block font-medium">Data wyjazdu</span>
                <span className="text-neutral-900 font-semibold">
                  {formatDate(checkOutDate) || "Wybierz datę"}
                </span>
              </div>
            </div>
            <div className="text-right flex items-center space-x-3">
              <div>
                {getDuration() && (
                  <div className="text-sm text-primary-600 font-medium">
                    {getDuration()}
                  </div>
                )}
                <div className="text-neutral-400 text-sm flex items-center">
                  <EditIcon className="mr-1 w-5 h-5" />
                  Kliknij, aby zmienić
                </div>
              </div>
              {checkInDate && checkOutDate && (
                <div className="text-success-500 flex items-center">
                  <CheckIcon />
                </div>
              )}
            </div>
          </div>
        </button>

        {showDatePicker && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-25 z-10"
              onClick={() => setShowDatePicker(false)}
            />
            <div className="absolute top-full left-0 right-0 z-20 mt-2">
              <DateRangePicker
                startDate={checkInDate}
                endDate={checkOutDate}
                onChange={handleDateChange}
              />
              <div className="mt-3 flex justify-end space-x-2 bg-white px-4 pb-4 rounded-b-xl border-t border-neutral-100">
                <button
                  type="button"
                  onClick={() => setShowDatePicker(false)}
                  className="px-3 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors duration-200 text-sm font-medium"
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={() => setShowDatePicker(false)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm font-medium"
                >
                  Zatwierdź daty
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DateRangeSelector;
